// Babi proactive check-ins: Weekly Recap + Daily Study Plan (dev backlog task 12's follow-on).
// Every function here is pure/deterministic — it only computes real numbers from profile/
// progress/cards/grammarProgress. Phrasing those numbers into a message is a separate concern
// (see phraseWeeklyRecap/phraseDailyPlan in api.js) so Babi is never asked to invent a stat.
import { addDays, daysBetween, cardDist } from "./utils";
import { BADGES, getBadge } from "./constants";
import { getTopicCount, getWordCount } from "./curriculum";
import { getGrammarPoints, isGrammarPointDue } from "./grammarLab";

// Reasonable milestones for "days remaining to a streak milestone" — no existing badge/
// milestone ladder for streaks specifically (BADGES in constants.js is word-count based).
const STREAK_MILESTONES = [7, 14, 30, 50, 100, 200, 365, 500, 1000];

// A week runs Sunday–Saturday. Mirrors the mixed UTC-parse/local-methods style
// addDays/daysBetween already use elsewhere in utils.js, for consistency.
export function getWeekStart(dateISO) {
  const d = new Date(dateISO);
  d.setDate(d.getDate() - d.getDay());
  return d.toISOString().slice(0, 10);
}

// The most recently fully-completed Sun-Sat week strictly before the week containing `today`.
export function getPriorCompleteWeek(todayISOStr) {
  const thisWeekStart = getWeekStart(todayISOStr);
  return { weekStart: addDays(thisWeekStart, -7), weekEnd: addDays(thisWeekStart, -1) };
}

// `profile.accountCreatedAt` is backfilled for every account (new signups write it directly;
// existing accounts get it backfilled once from Firebase Auth's real creation timestamp — see
// App.js onAuthChange). A profile that somehow still lacks it is treated as "not yet eligible"
// rather than guessing, so the Weekly Recap simply waits one more day for the backfill to land.
export function accountAgeDays(profile, todayISOStr) {
  if (!profile?.accountCreatedAt) return 0;
  return daysBetween(profile.accountCreatedAt, todayISOStr);
}

function groupDailyLogByWeek(dailyLog) {
  const map = new Map();
  for (const l of dailyLog || []) {
    const ws = getWeekStart(l.date);
    if (!map.has(ws)) map.set(ws, { words: 0, accuracySum: 0, accuracyCount: 0 });
    const g = map.get(ws);
    g.words += l.words || 0;
    if (typeof l.accuracy === "number") { g.accuracySum += l.accuracy; g.accuracyCount++; }
  }
  return map;
}

// All numbers here are real/computed — the caller (phraseWeeklyRecap) only ever phrases them.
export function computeWeeklyRecapStats(profile, progress, cards, weekStart, weekEnd) {
  const dailyLog = progress?.dailyLog || [];
  const weekLogs = dailyLog.filter(l => l.date >= weekStart && l.date <= weekEnd);
  const inactiveDays = 7 - weekLogs.length;
  const weekWords = weekLogs.reduce((s, l) => s + (l.words || 0), 0);
  const accuracyLogs = weekLogs.filter(l => typeof l.accuracy === "number");
  const weekAvgAccuracy = accuracyLogs.length
    ? accuracyLogs.reduce((s, l) => s + l.accuracy, 0) / accuracyLogs.length
    : null;

  // Personal records: only meaningful once there's enough prior history to compare against —
  // otherwise this week's number would trivially "break a record" against nothing.
  const weekMap = groupDailyLogByWeek(dailyLog);
  const priorWeeks = [...weekMap.entries()].filter(([ws]) => ws < weekStart);
  const enoughHistory = priorWeeks.length >= 3;
  const maxPriorWords = priorWeeks.length ? Math.max(...priorWeeks.map(([, g]) => g.words)) : 0;
  const priorAccuracyWeeks = priorWeeks.filter(([, g]) => g.accuracyCount > 0);
  const maxPriorAccuracy = priorAccuracyWeeks.length
    ? Math.max(...priorAccuracyWeeks.map(([, g]) => g.accuracySum / g.accuracyCount))
    : 0;
  const mostWordsRecordBroken = enoughHistory && weekWords > maxPriorWords;
  const bestRetentionRecordBroken = enoughHistory && weekAvgAccuracy != null && weekAvgAccuracy > maxPriorAccuracy;
  const isNewLongestStreakRecord = !!progress?.longestStreakAt
    && progress.longestStreakAt >= weekStart && progress.longestStreakAt <= weekEnd;

  const deeplyRetained = cardDist(cards)[3];
  const currentBadge = getBadge(deeplyRetained);
  const currentBadgeIdx = BADGES.findIndex(b => b.key === currentBadge.key);
  const nextBadge = BADGES[currentBadgeIdx + 1] || null;
  const wordsToNextBadgeTier = nextBadge ? Math.max(0, nextBadge.min - deeplyRetained) : null;

  const streak = progress?.streak || 0;
  const nextStreakMilestone = STREAK_MILESTONES.find(m => m > streak) || null;
  const daysToStreakMilestone = nextStreakMilestone ? nextStreakMilestone - streak : null;

  // Words that flipped to mastered within this exact window — d1MasteredAt/d2MasteredAt are
  // stamped in App.js's handleCardReview at the moment the flag flips (new instrumentation
  // added for this feature; older mastery flips predating it have no timestamp and are
  // correctly never claimed as "this week").
  const masteredThisWeek = (cards || [])
    .filter(c =>
      (c.d1MasteredAt && c.d1MasteredAt >= weekStart && c.d1MasteredAt <= weekEnd) ||
      (c.d2MasteredAt && c.d2MasteredAt >= weekStart && c.d2MasteredAt <= weekEnd)
    )
    .slice(0, 5)
    .map(c => ({ turkish: c.turkish, english: c.english }));

  // Pace check against the Goals page target — simple, transparent forward projection (task 18
  // doesn't exist yet as of this writing; reuse it here instead once/if it lands).
  const weeklyTargetWords = (profile?.wordsPerDay || 0) * 7;
  const paceOnTrack = weekWords >= weeklyTargetWords;
  const currentDailyPace = weekWords / 7;
  const remainingWords = Math.max(0, (profile?.targetWords || 0) - (progress?.wordsLearned || 0));
  const projectedDaysToGoal = currentDailyPace > 0 ? Math.ceil(remainingWords / currentDailyPace) : null;
  const projectedDate = projectedDaysToGoal != null ? addDays(weekEnd, projectedDaysToGoal) : null;

  return {
    weekStart, weekEnd, inactiveDays, weekWords, weekMinutes: weekLogs.reduce((s, l) => s + (l.minutes || 0), 0),
    weekAvgAccuracy,
    mostWordsRecordBroken, bestRetentionRecordBroken, isNewLongestStreakRecord,
    longestStreak: progress?.longestStreak || 0,
    nextBadgeKey: nextBadge?.key || null, wordsToNextBadgeTier,
    nextStreakMilestone, daysToStreakMilestone,
    masteredThisWeek,
    weeklyTargetWords: Math.round(weeklyTargetWords), paceOnTrack, projectedDate,
  };
}

// Sane bounds for a stated "how much time do you have today" answer — rejects nonsensical
// replies (e.g. "2 days", "18 hours", "30 seconds") regardless of which parser produced the
// number. Enforced in AgentChat.js after both parseMinutesFromText and the Haiku fallback.
export const MIN_STUDY_MINUTES = 5;
export const MAX_STUDY_MINUTES = 300;

// Persian/Arabic-Indic digits (۰-۹ / ٠-٩) → Western 0-9, so a bare-number reply typed in a
// native script (e.g. "۲۰" for a Farsi speaker) still matches the digit regexes below.
const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";
const ARABIC_INDIC_DIGITS = "٠١٢٣٤٥٦٧٨٩";
function normalizeDigits(s) {
  return s.replace(/[۰-۹٠-٩]/g, ch => {
    const pi = PERSIAN_DIGITS.indexOf(ch);
    if (pi !== -1) return String(pi);
    const ai = ARABIC_INDIC_DIGITS.indexOf(ch);
    return ai !== -1 ? String(ai) : ch;
  });
}

// Free-text "how much time do you have today" → minutes, or null if unparseable. English-word/
// digit heuristic only — deliberately cheap and instant for the common case (a plain number,
// in any digit script). Doesn't understand non-English time phrasing (e.g. Farsi "نیم ساعت") —
// callers should fall back to a Haiku-based extraction (api.js's extractStudyMinutes) when this
// returns null rather than treating null as "no time was ever stated."
export function parseMinutesFromText(text) {
  if (!text) return null;
  const s = normalizeDigits(text.trim()).toLowerCase();
  if (/\bhalf\s*(an\s*)?hour\b/.test(s)) return 30;
  const hourMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:hours?|hrs?|h)\b/);
  if (hourMatch) return Math.round(parseFloat(hourMatch[1]) * 60);
  if (/\b(an|1|one)\s*hour\b/.test(s)) return 60;
  const minMatch = s.match(/(\d+(?:\.\d+)?)\s*(?:minutes?|mins?|m)\b/);
  if (minMatch) return Math.round(parseFloat(minMatch[1]));
  // Only trust a bare number as "minutes" when the ENTIRE reply is just that number (plus
  // trivial trailing punctuation) — e.g. "20", "۲۰", "20.". If anything else is attached (a
  // unit word we don't recognize above, in ANY language — e.g. Farsi "4 ساعت" = "4 hours"),
  // we can't safely assume the unit is minutes. Silently guessing here previously misread
  // "4 hours" as "4 minutes" for any non-English unit word; falling through to null instead
  // routes it to the Haiku fallback (api.js's extractStudyMinutes), which actually understands
  // units in any language.
  const bareNumOnly = s.match(/^(\d+(?:\.\d+)?)[\s.!؟?]*$/);
  if (bareNumOnly) return Math.round(parseFloat(bareNumOnly[1]));
  return null;
}

// Sanity bounds on the derived seconds/card rate below, as a last-resort guard against bad
// data still producing an implausible plan (e.g. a mis-logged session).
const MIN_SECONDS_PER_CARD = 10;
const MAX_SECONDS_PER_CARD = 90;

// Per-user seconds/card, derived from D1 review time only (d1Minutes/d1Reviews — new
// per-deck instrumentation on dailyLog entries, App.js handleCardReview). D1 (listen, recall,
// reveal, then rate) is the slower of the two decks; D2 is a quick flip-and-rate, so blending
// the two into one average let fast D2 sessions drag the estimate down to something unusable
// (the "131 cards in 9 minutes" bug). Using D1 alone is simpler and, applied to both decks'
// due counts, errs conservative (slightly overestimates D2 time) rather than under.
// Being deck-specific, a day's d1Minutes is never contaminated by a same-day lesson or D2
// session, so no separate "pure review day" filter is needed here. Existing accounts won't
// have d1Minutes on old dailyLog entries, so this correctly falls back to the 30s/card default
// until enough new entries accumulate.
function deriveSecondsPerCard(progress) {
  const logs = (progress?.dailyLog || []).filter(l => (l.d1Reviews || 0) > 0 && (l.d1Minutes || 0) > 0);
  if (logs.length < 3) return null;
  const totalReviews = logs.reduce((s, l) => s + l.d1Reviews, 0);
  if (!totalReviews) return null;
  const raw = (logs.reduce((s, l) => s + l.d1Minutes, 0) * 60) / totalReviews;
  return Math.min(Math.max(raw, MIN_SECONDS_PER_CARD), MAX_SECONDS_PER_CARD);
}

// Per-user minutes/lesson. Same mixed-bucket caveat as deriveSecondsPerCard applies here too:
// a day with both a lesson AND flashcard reviews has dailyLog.minutes covering both, so this
// is a best-available approximation, not a clean split — acceptable per spec.
function deriveMinutesPerLesson(progress) {
  const logs = (progress?.dailyLog || []).filter(l => (l.lessons || 0) > 0 && (l.minutes || 0) > 0);
  if (logs.length < 3) return null;
  const totalLessons = logs.reduce((s, l) => s + l.lessons, 0);
  if (!totalLessons) return null;
  return logs.reduce((s, l) => s + l.minutes, 0) / totalLessons;
}

// Per-point/per-topic time estimates used by the waterfall below.
const GRAMMAR_MINUTES_PER_POINT = 20; // 15 min study + 5 min practice
const LISTEN_SPEAK_MINUTES_PER_TOPIC = 8;
// Caps on how far priority-3 items get stacked once flashcards+lessons+one-of-each are covered,
// and the thresholds the immersion (YouTube/TV) nudge is gated behind — see the "reserved for
// when there's enough time to do everything else" rule this waterfall implements.
const MAX_GRAMMAR_POINTS_PER_DAY = 2;
const MAX_LESSONS_FOR_IMMERSION_GATE = 3;
const MAX_LISTEN_SPEAK_FOR_IMMERSION_GATE = 3;
// When there isn't enough time left after a full flashcard review to fit even one other
// activity, flashcards get capped down to free up time — but never reduced so far that fewer
// than this many stay due (an approximate "up to 30-40 remain" floor, not an exact target).
const FLASHCARD_REDUCTION_FLOOR = 35;

// The Daily Study Plan waterfall.
//
// Priority order: (1) flashcards — reviewed in full unless that would leave no time for
// anything else today, in which case the review is capped down (leaving roughly
// FLASHCARD_REDUCTION_FLOOR cards due) to free up time; (2) new lessons, up to the day's word
// target; (3) one Grammar Lab point AND one Listen & Speak Lab topic (Elite-only), tied for
// priority and randomly ordered against each other; then, with any time still left over, more
// of lessons/grammar/Listen&Speak are stacked in (up to MAX_*_FOR_IMMERSION_GATE /
// MAX_GRAMMAR_POINTS_PER_DAY) before finally (4) a generic immersion nudge (YouTube/TV), which
// only appears once ALL due flashcards, 3 lessons, >=1 grammar point, and (Elite-only) 3
// Listen&Speak topics are already in the plan.
//
// hasListenSpeakAccess should be the same hasVoiceLabAccess(...) check the app uses to gate the
// real Listen & Speak Lab screen (constants.js), so this never suggests a feature the learner
// can't actually open. completedTopics is the learner's {topic_id: {...}} completion map — its
// size is the pool of topics eligible for Listen & Speak practice (any completed topic, no
// recency/ordering logic — there's no per-topic "last practiced in the Lab" tracking to sort by).
//
// Returns only real, already-computed numbers for phraseDailyPlan (api.js) to phrase — never
// asked to invent one.
export function computeDailyPlanWaterfall(profile, progress, cards, grammarProgress, availableMinutes, today, completedTopics, hasListenSpeakAccess) {
  const langCode = (profile?.targetLanguage || "Turkish").toLowerCase();
  const secondsPerCard = deriveSecondsPerCard(progress) || 30;
  // D1 and D2 are reviewed as separate queues (FlashcardView starts one deck at a time), and
  // the same card can be due in both at once — so this must sum the two counts, not dedupe by
  // card, or a card due in both decks only counts as one review instead of two.
  const d1DueCount = (cards || []).filter(c => !c.d1Mastered && c.due <= today).length;
  const d2DueCount = (cards || []).filter(c => c.d2 && !c.d2Mastered && c.d2.due <= today).length;
  const dueCount = d1DueCount + d2DueCount;
  const flashcardMinutesNeeded = Math.ceil((dueCount * secondsPerCard) / 60);

  const minutesPerLesson = deriveMinutesPerLesson(progress) || 10;
  const topicCount = getTopicCount(langCode);
  const avgWordsPerLesson = topicCount > 0 ? getWordCount(langCode) / topicCount : 15;
  const todayLog = (progress?.dailyLog || []).find(l => l.date === today);
  const wordsAlreadyToday = todayLog?.words || 0;
  const wordsNeededToday = Math.max(0, Math.round(profile?.wordsPerDay || 0) - wordsAlreadyToday);
  const lessonsNeeded = wordsNeededToday > 0 ? Math.ceil(wordsNeededToday / avgWordsPerLesson) : 0;
  const lessonMinutesNeeded = Math.ceil(lessonsNeeded * minutesPerLesson);

  const candidatePoints = getGrammarPoints(langCode)
    .filter(gp => isGrammarPointDue(grammarProgress?.[gp.grammar_point_id], today))
    .sort((a, b) => {
      const aSeen = grammarProgress?.[a.grammar_point_id]?.lastReviewed ? 1 : 0;
      const bSeen = grammarProgress?.[b.grammar_point_id]?.lastReviewed ? 1 : 0;
      return bSeen - aSeen; // previously-reviewed recap points before never-touched ones
    });
  const listenSpeakPoolSize = hasListenSpeakAccess ? Object.keys(completedTopics || {}).length : 0;

  // "True need" (uncapped) drives the effort nudge; the plan itself is capped to what fits.
  const cheapestOtherActivityMinutes = Math.min(
    lessonsNeeded > 0 ? minutesPerLesson : Infinity,
    candidatePoints.length > 0 ? GRAMMAR_MINUTES_PER_POINT : Infinity,
    listenSpeakPoolSize > 0 ? LISTEN_SPEAK_MINUTES_PER_TOPIC : Infinity,
  );
  const trueNeedMinutes = flashcardMinutesNeeded + lessonMinutesNeeded;
  const effortNudge = availableMinutes < trueNeedMinutes;
  const shortfallMinutes = effortNudge ? trueNeedMinutes - availableMinutes : 0;

  // Priority 1: flashcards. Reviewed in full UNLESS doing so would leave no time for even the
  // cheapest other activity today — in that case, cap the review down (never below leaving
  // FLASHCARD_REDUCTION_FLOOR cards due) to free up time for priority 2/3.
  let flashcardsToReview = Math.min(dueCount, Math.max(0, Math.floor((availableMinutes * 60) / secondsPerCard)));
  let flashcardMinutes = Math.ceil((flashcardsToReview * secondsPerCard) / 60);
  if (Number.isFinite(cheapestOtherActivityMinutes) && availableMinutes - flashcardMinutes < cheapestOtherActivityMinutes
    && flashcardsToReview > dueCount - FLASHCARD_REDUCTION_FLOOR) {
    const budgetMinutes = Math.max(0, availableMinutes - cheapestOtherActivityMinutes);
    flashcardsToReview = Math.max(0, Math.min(Math.floor((budgetMinutes * 60) / secondsPerCard), dueCount - FLASHCARD_REDUCTION_FLOOR));
    flashcardMinutes = Math.ceil((flashcardsToReview * secondsPerCard) / 60);
  }
  let remaining = Math.max(0, availableMinutes - flashcardMinutes);
  const allDueFlashcardsReviewed = flashcardsToReview === dueCount;

  // Priority 2: new lessons, up to today's word target.
  let lessonsToTake = lessonsNeeded;
  let lessonMinutes = lessonMinutesNeeded;
  if (lessonMinutesNeeded > remaining) {
    lessonsToTake = Math.max(0, Math.floor(remaining / minutesPerLesson));
    lessonMinutes = lessonsToTake * minutesPerLesson;
  }
  remaining = Math.max(0, remaining - lessonMinutes);

  // Priority 3: one Grammar Lab point and one Listen & Speak Lab topic, tied — randomly ordered
  // against each other so Babi doesn't always lead with the same one.
  let grammarCount = 0;
  let listenSpeakTopics = 0;
  const priority3 = Math.random() < 0.5 ? ["grammar", "listenSpeak"] : ["listenSpeak", "grammar"];
  for (const item of priority3) {
    if (item === "grammar" && grammarCount < Math.min(1, candidatePoints.length) && remaining >= GRAMMAR_MINUTES_PER_POINT) {
      grammarCount += 1;
      remaining -= GRAMMAR_MINUTES_PER_POINT;
    } else if (item === "listenSpeak" && listenSpeakTopics < Math.min(1, listenSpeakPoolSize) && remaining >= LISTEN_SPEAK_MINUTES_PER_TOPIC) {
      listenSpeakTopics += 1;
      remaining -= LISTEN_SPEAK_MINUTES_PER_TOPIC;
    }
  }

  // Extension: with any time still left over, keep stacking more lessons/grammar/Listen&Speak
  // (up to their per-day caps) before falling through to the immersion nudge.
  let stacked = true;
  while (stacked) {
    stacked = false;
    if (lessonsToTake < MAX_LESSONS_FOR_IMMERSION_GATE && remaining >= minutesPerLesson) {
      lessonsToTake += 1; lessonMinutes += minutesPerLesson; remaining -= minutesPerLesson; stacked = true;
    }
    if (grammarCount < Math.min(MAX_GRAMMAR_POINTS_PER_DAY, candidatePoints.length) && remaining >= GRAMMAR_MINUTES_PER_POINT) {
      grammarCount += 1; remaining -= GRAMMAR_MINUTES_PER_POINT; stacked = true;
    }
    if (listenSpeakTopics < Math.min(MAX_LISTEN_SPEAK_FOR_IMMERSION_GATE, listenSpeakPoolSize) && remaining >= LISTEN_SPEAK_MINUTES_PER_TOPIC) {
      listenSpeakTopics += 1; remaining -= LISTEN_SPEAK_MINUTES_PER_TOPIC; stacked = true;
    }
  }

  const grammarPoints = candidatePoints.slice(0, grammarCount).map(gp => ({
    id: gp.grammar_point_id, title: gp.title,
  }));

  // Priority 4 (last): the generic immersion nudge, reserved for once the plan already covers
  // everything else — all due flashcards, 3 lessons, >=1 grammar point, and (Elite-only) 3
  // Listen & Speak topics — with time still left over.
  const meetsImmersionGate = allDueFlashcardsReviewed
    && lessonsToTake >= MAX_LESSONS_FOR_IMMERSION_GATE
    && grammarPoints.length >= 1
    && (hasListenSpeakAccess ? listenSpeakTopics >= MAX_LISTEN_SPEAK_FOR_IMMERSION_GATE : true);
  const showImmersionNudge = remaining > 0 && meetsImmersionGate;

  return {
    availableMinutes, dueCount, flashcardsToReview, flashcardMinutes,
    lessonsToTake, lessonMinutes,
    grammarPoints, grammarMinutes: grammarPoints.length * GRAMMAR_MINUTES_PER_POINT,
    listenSpeakTopics, listenSpeakMinutes: listenSpeakTopics * LISTEN_SPEAK_MINUTES_PER_TOPIC,
    priority3Order: priority3,
    showImmersionNudge, remainingMinutes: remaining,
    effortNudge, shortfallMinutes,
  };
}
